import { Outlet, useParams, useRouterState } from '@tanstack/react-router';
import type { Me, Project } from '@hangar/shared';
import { useMe, useProjects } from '@/api/hooks';
import { useAuth } from '@/auth/AuthProvider';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { rememberAudience } from '@/lib/audience';
import { RunBuildSheet } from '@/features/builds/RunBuildSheet';
import { MobileTabBar, MobileTopBar, RunBuildFab } from './MobileBars';
import { RunBuildProvider } from './RunBuildContext';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { sectionOf, viewOf, type Section, type ShellView } from './nav';
import { PreviewBanner } from './PreviewBanner';
import { previousView, rememberView } from './useOrgActions';
import { NoAccess } from '@/routes/NoAccess';
import { OrgSuspended } from '@/routes/OrgSuspended';
import { previewTarget } from '@/api/orgSelection';
import { buildCrumbs, isAccessRefused, isOrgSuspended, pickProject, showsRunBuildFab, type Crumb } from './shellModel';
import { useOrgActions } from './useOrgActions';

/** What the chrome needs: who is signed in, the projects, the one in the URL and the breadcrumb trail. */
function useShellData() {
  const me = useMe();
  const projects = useProjects();
  const params = useParams({ strict: false }) as { slug?: string; buildId?: string; app?: string };
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentProject = pickProject(projects.data, params.slug);
  const section = sectionOf(pathname);
  const crumbs = buildCrumbs({ orgName: me.data?.org.name, project: currentProject, section, buildId: params.buildId, app: params.app });
  rememberAudience(me.data);
  const view = rememberView(viewOf(section, me.data?.user?.platformRole === 'operator', previousView()));
  return { me, projects, currentProject, crumbs, section, view };
}

interface ChromeProps {
  bp: ReturnType<typeof useBreakpoint>;
  me: Me | undefined;
  meError: boolean;
  projects: Project[] | undefined;
  currentProject: Project | undefined;
  view: ShellView;
  onSignOut: () => void;
}

/** The left/top navigation: the phone top bar, or the sidebar (a rail on tablet). */
function ShellNav({ bp, me, meError, projects, currentProject, view, onSignOut }: ChromeProps) {
  if (bp === 'phone') return <MobileTopBar me={me} project={currentProject} onSignOut={onSignOut} />;
  return <Sidebar rail={bp === 'tablet'} me={me} meError={meError} projects={projects} currentProject={currentProject} view={view} onSignOut={onSignOut} />;
}

/** Everything to the right of the sidebar: the desktop top bar and the page. */
function ShellContent({ phone, crumbs, section, project }: { phone: boolean; crumbs: Crumb[]; section: Section; project: Project | undefined }) {
  return (
    <div className={phone ? 'contents' : 'flex min-w-0 flex-1 flex-col'}>
      {phone ? null : <TopBar crumbs={crumbs} section={section} projectSlug={project?.slug} projectId={project?.id} projectName={project?.name} />}
      <main className={phone ? 'px-4 py-4' : 'mx-auto w-full max-w-[1440px] flex-1 px-6 py-6'}>
        <Outlet />
      </main>
    </div>
  );
}

function PhoneOverlays({ project, section }: { project: Project | undefined; section: Section }) {
  return (
    <>
      {showsRunBuildFab(section) ? <RunBuildFab projectSlug={project?.slug} projectId={project?.id} /> : null}
      <MobileTabBar currentSlug={project?.slug} />
    </>
  );
}

/**
 * Authenticated shell (screen 01): 240 px sidebar on desktop, 72 px icon rail on tablet (≤ 1024), top app bar +
 * bottom tab bar on phones (≤ 640). One chrome variant is mounted at a time (useBreakpoint), so testids never duplicate;
 * the page itself never remounts when the breakpoint changes (see below).
 * The Run build sheet is mounted once here and opened from the top bar, the phone FAB or any page.
 */
export function AppShell() {
  const bp = useBreakpoint();
  const { signOut, user } = useAuth();
  const { me, projects, currentProject, crumbs, section, view } = useShellData();
  const { exitPreview } = useOrgActions();
  if (isOrgSuspended(me.error)) {
    const preview = previewTarget();
    return <OrgSuspended email={user?.profile.email} requestId={me.error.requestId} onSignOut={() => void signOut()} preview={preview ? { name: preview.name, onExit: exitPreview } : undefined} />;
  }
  if (isAccessRefused(me.error)) {
    return <NoAccess email={user?.profile.email} requestId={me.error.requestId} onSignOut={() => void signOut()} />;
  }

  // One tree for every breakpoint: only the chrome (bars, sidebar) swaps. `<main>`, the `<Outlet />` and the Run build
  // sheet keep the same position, so crossing 641 px (a phone rotated to landscape, a narrowed window, iPad split view)
  // never remounts the page — the selected tab, a half-filled sheet and the scroll position survive.
  const phone = bp === 'phone';
  return (
    <RunBuildProvider>
      <PreviewBanner me={me.data} />
      <div className={phone ? 'min-h-dvh bg-background pb-[calc(96px+env(safe-area-inset-bottom))]' : 'flex min-h-dvh bg-background'}>
        <ShellNav bp={bp} me={me.data} meError={me.isError} projects={projects.data} currentProject={currentProject} view={view} onSignOut={() => void signOut()} />
        <ShellContent phone={phone} crumbs={crumbs} section={section} project={currentProject} />
        {phone ? <PhoneOverlays project={currentProject} section={section} /> : null}
      </div>
      <RunBuildSheet />
    </RunBuildProvider>
  );
}
